/**
 * Dialog — accessible modal built on Radix Dialog (focus trap, Esc to close,
 * labelled by its title). Use for forms and previews; ConfirmDialog for
 * yes/no decisions.
 */

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

const SIZES = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl',
} as const;

const FIRST_FIELD = [
  'input:not([type="hidden"]):not([disabled]):not([readonly])',
  'textarea:not([disabled]):not([readonly])',
  'select:not([disabled])',
  '[role="combobox"]:not([disabled])',
].join(', ');

export interface DialogContentProps extends Omit<React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>, 'title'> {
  title: React.ReactNode;
  description?: React.ReactNode;
  size?: keyof typeof SIZES;
  footer?: React.ReactNode;
}

export const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, DialogContentProps>(
  ({ title, description, size = 'md', footer, className, children, onOpenAutoFocus, ...props }, ref) => {
    const { t } = useTranslation('common');
    const bodyRef = React.useRef<HTMLDivElement>(null);
    // Open on the first field (ready to type), not on the close button — and never ring the X on open.
    const focusFirstField = (event: Event) => {
      onOpenAutoFocus?.(event);
      if (event.defaultPrevented) return;
      event.preventDefault();
      const first = bodyRef.current?.querySelector<HTMLElement>(FIRST_FIELD);
      (first ?? (event.currentTarget as HTMLElement | null))?.focus({ preventScroll: true });
    };
    return (
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          ref={ref}
          {...(description ? {} : { 'aria-describedby': undefined })}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 flex max-h-[90vh] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg border border-border bg-card text-card-foreground shadow-soft focus:outline-none',
            SIZES[size],
            className,
          )}
          onOpenAutoFocus={focusFirstField}
          {...props}
        >
          <div className="flex items-start justify-between gap-4 border-b border-border/70 px-6 py-4">
            <div className="min-w-0 space-y-1">
              <DialogPrimitive.Title className="font-display text-xl font-semibold leading-snug">{title}</DialogPrimitive.Title>
              {description ? <DialogPrimitive.Description className="text-sm text-muted-foreground">{description}</DialogPrimitive.Description> : null}
            </div>
            <DialogPrimitive.Close className="focus-ring rounded-md p-1 text-muted-foreground hover:text-foreground" aria-label={t('aria.close')}>
              <X className="h-5 w-5" />
            </DialogPrimitive.Close>
          </div>
          <div ref={bodyRef} className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            {children}
          </div>
          {footer ? <div className="flex flex-wrap justify-end gap-2 border-t border-border/70 px-6 py-4">{footer}</div> : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    );
  },
);
DialogContent.displayName = 'DialogContent';
